// src/components/internal/PickParticipantsModal.jsx
import { useEffect, useMemo, useState } from "react";
import { internalApi } from "../../api/internal";

export default function PickParticipantsModal({
  open,
  onClose,
  onConfirm,
  excludeUserId,
}) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;

    setQ("");
    setSelected(new Set());

    (async () => {
      setLoading(true);
      try {
        const { data } = await internalApi.listUsers("");
        const rows = data?.rows || [];
        setUsers(rows.filter((u) => Number(u.id) !== Number(excludeUserId)));
      } catch (e) {
        setUsers([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, excludeUserId]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return users;
    return (users || []).filter((u) =>
      `${u.name || ""} ${u.role || ""} ${u.email || ""}`
        .toLowerCase()
        .includes(qq)
    );
  }, [q, users]);

  const selectedCount = selected.size;

  const toggle = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const clearAll = () => setSelected(new Set());
  const selectAllFiltered = () =>
    setSelected(new Set(filtered.map((u) => u.id)));

  const initials = (name = "", fallback = "") => {
    const n = String(name || "").trim();
    if (!n) return String(fallback || "?").slice(0, 2).toUpperCase();
    const parts = n.split(/\s+/).filter(Boolean);
    const a = (parts[0]?.[0] || "").toUpperCase();
    const b = (parts[1]?.[0] || "").toUpperCase();
    return (a + b) || a || "?";
  };

  const rolePill = (role = "") => {
    const r = String(role || "").toLowerCase();
    if (r.includes("admin") || r.includes("owner"))
      return "bg-emerald-500/10 border-emerald-400/20 text-emerald-200";
    if (r.includes("super"))
      return "bg-violet-500/10 border-violet-400/20 text-violet-200";
    if (r.includes("agent"))
      return "bg-sky-500/10 border-sky-400/20 text-sky-200";
    return "bg-slate-500/10 border-slate-400/20 text-slate-200";
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        // click afuera para cerrar
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />

      {/* modal */}
      <div className="relative w-[720px] max-w-[96vw] overflow-hidden rounded-2xl border border-white/10 bg-[#0f172a] shadow-2xl">
        {/* header */}
        <div className="flex items-start justify-between gap-3 border-b border-white/10 px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 ring-1 ring-white/10">
                <svg viewBox="0 0 24 24" className="h-5 w-5 text-slate-200">
                  <path
                    d="M16 11c1.66 0 3-1.57 3-3.5S17.66 4 16 4s-3 1.57-3 3.5S14.34 11 16 11zm-8 0c1.66 0 3-1.57 3-3.5S9.66 4 8 4 5 5.57 5 7.5 6.34 11 8 11zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45v2h6v-2c0-2.66-5.33-4-7-4z"
                    fill="currentColor"
                  />
                </svg>
              </span>

              <div className="min-w-0">
                <div className="truncate text-base font-extrabold tracking-tight text-white">
                  Agregar participantes
                </div>
                <div className="text-xs text-slate-400">
                  Selecciona uno o varios usuarios para incluirlos en la conversación.
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
            aria-label="Cerrar"
            title="Cerrar"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5">
              <path
                d="M6 6l12 12M18 6l-12 12"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* search + actions */}
        <div className="flex flex-wrap items-center gap-2 border-b border-white/10 px-5 py-3">
          <div className="relative flex-1 min-w-[240px]">
            <svg
              viewBox="0 0 24 24"
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            >
              <path
                d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15z"
                stroke="currentColor"
                strokeWidth="1.8"
                fill="none"
                strokeLinecap="round"
              />
            </svg>
            <input
              className="h-10 w-full rounded-xl border border-white/10 bg-white/5 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-400 outline-none focus:border-sky-400/40 focus:ring-4 focus:ring-sky-500/10"
              placeholder="Buscar por nombre, rol o email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={selectAllFiltered}
              disabled={loading || filtered.length === 0}
              className="h-10 rounded-xl border border-white/10 bg-white/5 px-3 text-xs font-semibold text-slate-100 hover:bg-white/10 disabled:opacity-50"
              title="Seleccionar todos los resultados"
            >
              Seleccionar todo
            </button>
            <button
              type="button"
              onClick={clearAll}
              disabled={selectedCount === 0}
              className="h-10 rounded-xl border border-white/10 bg-white/5 px-3 text-xs font-semibold text-slate-100 hover:bg-white/10 disabled:opacity-50"
              title="Limpiar selección"
            >
              Limpiar
            </button>
          </div>
        </div>

        {/* list */}
        <div className="max-h-[62vh] overflow-auto">
          {loading ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400">
              <div className="mx-auto mb-3 h-5 w-5 animate-spin rounded-full border-2 border-white/10 border-t-white/50" />
              Cargando usuarios…
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400">
              Sin resultados.
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filtered.map((u) => {
                const on = selected.has(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(u.id)}
                    className={`w-full px-5 py-3 text-left transition ${
                      on ? "bg-sky-500/10" : "hover:bg-white/5"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* checkbox */}
                      <span
                        className={`inline-flex h-5 w-5 flex-none items-center justify-center rounded-md border text-[10px] font-black ${
                          on
                            ? "border-sky-400/40 bg-sky-500/20 text-sky-100"
                            : "border-white/15 bg-white/5 text-transparent"
                        }`}
                        aria-hidden="true"
                      >
                        ✓
                      </span>

                      {/* avatar */}
                      <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-white/5 ring-1 ring-white/10 text-sm font-extrabold text-slate-200">
                        {initials(u.name, u.id)}
                      </div>

                      {/* text */}
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="truncate font-semibold text-slate-100">
                            {u.name || `User #${u.id}`}
                          </div>
                          {u.role ? (
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-[2px] text-[10px] font-extrabold uppercase tracking-wide ${rolePill(
                                u.role
                              )}`}
                            >
                              {u.role}
                            </span>
                          ) : null}
                        </div>
                        <div className="truncate text-xs text-slate-400">
                          {u.email || "—"}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* footer (sticky) */}
        <div className="border-t border-white/10 bg-[#0b1220]/60 px-5 py-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-slate-400">
              Seleccionados:{" "}
              <span className="font-extrabold text-slate-100">{selectedCount}</span>
              {q.trim() ? (
                <>
                  {" "}
                  · Resultados:{" "}
                  <span className="font-extrabold text-slate-100">{filtered.length}</span>
                </>
              ) : null}
            </div>

            <div className="flex items-center gap-2">
              <button
                className="h-10 rounded-xl border border-white/10 bg-white/5 px-4 text-sm font-semibold text-slate-100 hover:bg-white/10"
                onClick={onClose}
                type="button"
              >
                Omitir
              </button>

              <button
                className="h-10 rounded-xl bg-sky-500 px-4 text-sm font-extrabold text-white shadow-lg shadow-sky-500/20 hover:bg-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => onConfirm(Array.from(selected))}
                disabled={selectedCount === 0}
                type="button"
              >
                Agregar ({selectedCount})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
